import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { InvariantCheckCode, PageDetail } from 'shared';
import { api } from '../api';
import { Note } from './Note';
import { tokens } from '../theme';

/**
 * Bật/tắt sáu luật bất biến cho một trang — spec SC-01 §5.
 *
 * Nhãn và mô tả khai LẠI ở FE thay vì import từ `packages/shared`: gói đó biên
 * dịch ra CommonJS nên Rollup không phân giải được giá trị lúc chạy khi nó đi
 * qua `export *` — FE chỉ import KIỂU được. Cùng lý do với `flow-steps.ts`.
 * Danh sách mã thì vẫn do `shared` quyết định; ở đây chỉ là phần chữ hiển thị.
 */
const LUAT: { code: InvariantCheckCode; label: string; hint: string }[] = [
  {
    code: 'cta-target',
    label: 'CTA trỏ /contact',
    hint: 'Site Splus gom mọi nút bấm về đúng một trang liên hệ chung.',
  },
  {
    code: 'contact-widget',
    label: 'Widget hỗ trợ',
    hint: 'Mọi trang sản phẩm của Splus đều nhúng contact-widget.js.',
  },
  {
    code: 'bilingual',
    label: 'Cặp data-vi / data-en',
    hint: 'Trang Splus song ngữ: mỗi đoạn tiếng Việt có một đoạn tiếng Anh đi kèm.',
  },
  {
    code: 'lang-storage-key',
    label: 'Khoá localStorage theo slug',
    hint: 'Mỗi trang nhớ lựa chọn ngôn ngữ riêng, không đè lên trang khác.',
  },
  {
    code: 'footer-standard',
    label: 'Footer email và bản quyền',
    hint: 'Chân trang dùng đúng email và dòng bản quyền chuẩn của công ty.',
  },
  {
    code: 'hub-product-count',
    label: 'Số đếm sản phẩm trên hub',
    hint: 'Số sản phẩm ghi trên trang tổng khớp số trang thật sự có.',
  },
];

export function InvariantSettingsDialog({
  detail,
  open,
  onClose,
}: {
  detail: PageDetail;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [bat, setBat] = useState<InvariantCheckCode[]>(detail.invariantChecks);

  // Mở lại hộp thoại phải thấy trạng thái ĐANG LƯU, không phải trạng thái dở
  // dang của lần mở trước mà người dùng đã bấm Huỷ.
  useEffect(() => {
    if (open) setBat(detail.invariantChecks);
  }, [open, detail.invariantChecks]);

  const luu = useMutation({
    mutationFn: () => api.updatePage(detail.slug, { invariantChecks: bat }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page', detail.slug] });
      void qc.invalidateQueries({ queryKey: ['registry'] });
      onClose();
    },
  });

  const doi = (code: InvariantCheckCode) =>
    setBat((cu) => (cu.includes(code) ? cu.filter((c) => c !== code) : [...cu, code]));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 18 }}>Luật riêng của site Splus</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Note tone="warn">
            Sáu luật này là quy ước riêng của website Splus, không phải luật SEO chung. Bật cho
            trang không thuộc Splus sẽ báo lỗi sai hàng loạt.
          </Note>
          <Note>
            Luật bất biến <strong>không ảnh hưởng điểm SEO</strong> — đây là bảng tuân thủ, không
            phải chỉ số. Bật hay tắt thì điểm vẫn y nguyên.
          </Note>

          {LUAT.map((l) => (
            <Stack key={l.code} direction="row" spacing={1.5} alignItems="flex-start">
              <Switch
                size="small"
                checked={bat.includes(l.code)}
                onChange={() => doi(l.code)}
                inputProps={{ 'aria-label': l.label }}
                sx={{ mt: '-2px' }}
              />
              <div>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>
                  {l.label}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{l.hint}</Typography>
              </div>
            </Stack>
          ))}

          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            Thay đổi áp dụng từ lần quét kế tiếp. Lịch sử điểm cũ giữ nguyên.
          </Typography>

          {luu.error && <Note tone="bad">{(luu.error as Error).message}</Note>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Huỷ
        </Button>
        <Button variant="contained" disabled={luu.isPending} onClick={() => luu.mutate()}>
          {luu.isPending ? 'Đang lưu…' : 'Lưu thiết lập'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
